import { Toolbar, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";
import DeleteConfimrationModal from "../../../../../common/DeleteConfirmationModal";
import RatePlanEditor from "./RatePlanEditor";
import DataGrid from "../../../../../common/DataGrid";
import { CreateButton } from "../../../../../common/Buttons";
import { DeleteIcon, EditIcon, RestrictionsButton } from "../../../../../common/icons/Icons";
import PaymentRulesEditor from "./PaymentRulesEditor";
import RestrictionsEditor from "./RestrictionsEditor";
import { usePlansData } from "../../../../../common/hooks/useRatePlansData";

const ActionsCell = ({ entityId, onEditClick, onDeleteClick, onEditPaymentRulesClick, onEditRestrictionsClick }) => {
    const onClick = (evt, callback) => {
        evt.preventDefault();

        callback(entityId);
    }

    return <Box className='actionsCell' sx={{ display: 'flex', flexDirection: 'row' }} >
        <EditIcon onClick={(evt) => onClick(evt, onEditClick)} />
        <RestrictionsButton onClick={(evt) => onClick(evt, onEditRestrictionsClick)} title='Edit restrictions' />
        <DeleteIcon onClick={(evt) => onClick(evt, onDeleteClick)} />
    </Box >
}

//         <PaymentsIcon onClick={(evt) => onClick(evt, onEditPaymentRulesClick)} title='Edit payment rules' />


const RatePlansList = ({ propertyId, ratePlans, onCreated, onUpdated, onDeleted, onError }) => {
    const [editorOpen, setEditorOpen] = useState(false);
    const [editedRatePlan, setEditedRatePlan] = useState(null);
    const [deletedRatePlan, setDeletedRatePlan] = useState(null);
    const [paymentRulesEditorOpen, setPaymentRulesEditorOpen] = useState(false);
    const [restrictionsditorOpen, setRestrictionsEditorOpen] = useState(false);

    const { deletePlan } = usePlansData(propertyId);

    const openEditor = () => {
        setEditorOpen(true);
    }

    const closeEditor = () => {
        setEditorOpen(false);
    }

    const onAdd = () => {
        setEditedRatePlan(null);
        openEditor();
    };

    const onCreateSuccess = (createdPlan) => {
        closeEditor();

        onCreated(createdPlan);
    }

    const onUpdateSuccess = (updatedPlan) => {
        closeEditor();

        onUpdated(updatedPlan);
    }

    const onClose = () => {
        closeEditor();
    }

    const onDelete = (ratePlanId) => {
        const ratePlan = ratePlans.filter((ratePlan) => ratePlan.id === ratePlanId)[0];
        setDeletedRatePlan(ratePlan);
    }

    const closeRestrictionsEditor = () => {
        setRestrictionsEditorOpen(false);
    }

    const openRestrictionsEditor = () => {
        setRestrictionsEditorOpen(true);
    }

    const openPaymentRulesEditor = () => {
        setPaymentRulesEditorOpen(true);
    }

    const closePaymentRulesEditor = () => {
        setPaymentRulesEditorOpen(false);
    }

    const onDeleteConfirmed = async (ratePlanId) => {
        setDeletedRatePlan(null);
        try {
            await deletePlan(ratePlanId);
            onDeleted(ratePlanId);
        }
        catch (error) {
            onError(error);
        }
    }

    const onEdit = (ratePlanId) => {
        const ratePlan = ratePlans.find(ratePlan => ratePlan.id === ratePlanId);

        setEditedRatePlan(ratePlan);
        openEditor();
    }

    const onPaymentRulesEdit = (ratePlanId) => {
        const ratePlan = ratePlans.find(ratePlan => ratePlan.id === ratePlanId);

        setEditedRatePlan(ratePlan);
        openPaymentRulesEditor();
    }

    const onRestrictionsEdit = (ratePlanId) => {
        const ratePlan = ratePlans.find(ratePlan => ratePlan.id === ratePlanId);

        setEditedRatePlan(ratePlan);
        openRestrictionsEditor();
    }

    const gridColumns = [
        {
            field: 'id',
            headerName: 'ID',
            disableColumnMenu: true,
            sortable: false,
            hide: true
        },
        {
            field: 'name',
            headerName: 'Name',
            minWidth: 150,
            flex: 1
        },
        {
            field: 'description',
            headerName: 'Description',
            minWidth: 300,
            flex: 2
        },
        {
            field: 'actions',
            headerName: '',
            width: 300,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params) => {
                return <ActionsCell
                    entityId={params.row.id}
                    onEditClick={onEdit}
                    onDeleteClick={onDelete}
                    onEditPaymentRulesClick={onPaymentRulesEdit}
                    onEditRestrictionsClick={onRestrictionsEdit}
                />
            }
        }
    ];

    if (!ratePlans) {
        return null;
    }

    let ratePlansList = null;

    if (!ratePlans.length) {
        ratePlansList = <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
                variant="h6"
                component="div"
                sx={{ margin: '20px', textAlign: 'center' }}
            >
                It looks like you haven't added any Rate Plans yet
            </Typography>
            <CreateButton sx={{ width: '300px', height: '50px', alignSelf: 'center' }} onClick={onAdd} text='Add' />
        </Box>;
    }
    else {
        ratePlansList = <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Toolbar>
                <Box sx={{ flexGrow: 1 }} />
                <CreateButton onClick={onAdd} text='Add' />
            </Toolbar>
            <DataGrid
                rows={ratePlans}
                columns={gridColumns}
                disableSelectionOnClick
                autoHeight
            />
        </Box>
    }

    return <>
        {ratePlansList}
        {editorOpen && <RatePlanEditor propertyId={propertyId} onSuccess={editedRatePlan ? onUpdateSuccess : onCreateSuccess} onClose={onClose} existingRatePlans={ratePlans} editedRatePlan={editedRatePlan} onError={onError} />}
        {paymentRulesEditorOpen && <PaymentRulesEditor propertyId={propertyId} onSuccess={editedRatePlan ? onUpdateSuccess : onCreateSuccess} onClose={closePaymentRulesEditor} existingRatePlans={ratePlans} editedRatePlan={editedRatePlan} onError={onError} />}
        {restrictionsditorOpen && <RestrictionsEditor propertyId={propertyId} onClose={closeRestrictionsEditor} ratePlan={editedRatePlan} onError={onError} />}
        <DeleteConfimrationModal entity={deletedRatePlan} onCancel={() => setDeletedRatePlan(null)} onConfirm={onDeleteConfirmed} />
    </>;
}

export default RatePlansList;